import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CameraIcon, PersonCircleIcon, PenIcon, XIcon } from "assets/icons";
import { Button, Card } from "components/atoms";
import { DisplayEditToggle, TextInput } from "components/molecules";
import { useUserData } from "hooks";
import "./ProfileHeader.scss";

interface ProfileHeaderProps {
  photoUrl: string | null;
  onPhotoUploadClick: () => void;
  onPhotoDeleteClick: () => void;
  refreshProfilePhotoUrl: () => void;
}

const profileHeaderSchema = Yup.object({
  userDisplayName: Yup.string().required("Please choose a display name"),
});

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  photoUrl,
  onPhotoUploadClick,
  onPhotoDeleteClick,
  refreshProfilePhotoUrl,
}) => {
  const { userData, updateUserData } = useUserData();
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(photoUrl);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    setCurrentPhotoUrl(photoUrl);
  }, [photoUrl]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshProfilePhotoUrl();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshProfilePhotoUrl]);

  const profileHeaderFormik = useFormik({
    initialValues: {
      userDisplayName: "",
      profilePictureUrl: "",
    },
    validationSchema: profileHeaderSchema,
    onSubmit: async (values) => {
      const success = await handleSubmitProfileHeader(values);
      if (success) {
        setIsEditing(false);
      }
    },
  });

  const handleSubmitProfileHeader = async (
    values: Record<string, any>
  ): Promise<boolean> => {
    if (!userData?.id) {
      console.error("User ID is undefined. Cannot update profile.");
      return false;
    }

    try {
      const input = {
        id: userData?.id,
        userDisplayName: values.userDisplayName,
        profilePictureUrl: values?.profilePictureUrl,
      };

      const success = await updateUserData(input);
      if (success) {
        console.log(`Updated successfully.`);
        return true;
      } else {
        console.log(`Failed to update. Please try again.`);
        return false;
      }
    } catch (error) {
      console.error(`Error updating:`, error);
      console.log(`An error occurred while updating.`);
      return false;
    }
  };

  const MemoizedProfileImage = useMemo(() => {
    if (!currentPhotoUrl) return null;

    return (
      <img
        src={currentPhotoUrl}
        alt="Profile"
        onError={() => {
          refreshProfilePhotoUrl();
        }}
        style={{ width: "160px", height: "160px", borderRadius: "50%" }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPhotoUrl]);

  const DisplayHeaderComponent = () => (
    <>
      {photoUrl ? (
        MemoizedProfileImage
      ) : (
        <div className="person-icon">
          <PersonCircleIcon />
        </div>
      )}
      <Button
        type="button"
        className="upload-button"
        data-testid="upload-photo-button"
        text={<CameraIcon />}
        width="100%"
        variant="borderless"
        onClick={onPhotoUploadClick}
      />
      {photoUrl && (
        <Button
          type="button"
          className="delete-button"
          data-testid="delete-photo-button"
          text={<XIcon />}
          width="100%"
          variant="borderless"
          onClick={onPhotoDeleteClick}
        />
      )}
    </>
  );

  const DisplayContentComponent = (
    <>
      <h2>
        {userData?.userDisplayName}{" "}
        <Button
          text={<PenIcon />}
          className="edit-button"
          data-testid="edit-profile-button"
          onClick={() => setIsEditing(!isEditing)}
          width="2rem"
          variant="borderless"
        />
      </h2>
      <p>
        {userData?.userType !== "STUDENT" ? (
          <>
            {userData?.userType} / {userData?.domain}
          </>
        ) : (
          userData?.userType
        )}
      </p>
    </>
  );

  const EditContentComponent = (
    <form onSubmit={profileHeaderFormik.handleSubmit}>
      <TextInput
        id="userDisplayName"
        placeholder="Please enter a display name."
        getFieldProps={profileHeaderFormik.getFieldProps}
        error={profileHeaderFormik.errors.userDisplayName}
        touched={profileHeaderFormik.touched.userDisplayName}
      />
      <div className="button-group">
        <Button type="submit" text="Save" width="6rem" variant="darkPrimary" />
        <Button
          type="button"
          text="Cancel"
          width="6rem"
          variant="border"
          onClick={() => setIsEditing(false)}
        />
      </div>
    </form>
  );

  return (
    <>
      <Card
        className="profile-header"
        headerContent={
          <div className="header-content">
            <DisplayHeaderComponent />
          </div>
        }
        description={
          <div className="description-content">
            <DisplayEditToggle
              displayComponent={DisplayContentComponent}
              editComponent={EditContentComponent}
              isEditing={isEditing}
            />
          </div>
        }
      />
    </>
  );
};

export default ProfileHeader;
